<template>
  <div class="analytics-page">
    <div class="page-header">
      <div class="header-content">
        <h1><i class="fas fa-chart-line"></i> Analytics</h1>
        <p>Email extraction quality trends and performance metrics</p>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters-bar">
      <div class="filter-group">
        <label>From</label>
        <input v-model="from" type="date" class="form-control" :max="to || today" />
      </div>
      <div class="filter-group">
        <label>To</label>
        <input v-model="to" type="date" class="form-control" :min="from" :max="today" />
      </div>
      <div class="filter-group">
        <label>Mailbox</label>
        <select v-model="selectedEmailAccountId" class="form-control">
          <option :value="null">All mailboxes</option>
          <option v-for="acc in emailAccounts" :key="acc.id" :value="acc.id">
            {{ acc.label || acc.email }}
          </option>
        </select>
      </div>
      <div class="filter-actions">
        <button class="btn btn-primary" @click="loadAnalytics" :disabled="!from || !to || store.loading">
          <i :class="store.loading ? 'fas fa-spinner fa-spin' : 'fas fa-search'"></i>
          {{ store.loading ? 'Loading…' : 'Load' }}
        </button>
        <button class="btn btn-secondary" @click="exportCsv" :disabled="!store.dataPoints.length" title="Export CSV">
          <i class="fas fa-download"></i> Export CSV
        </button>
      </div>
    </div>

    <div v-if="rangeError" class="alert alert-error">
      <i class="fas fa-exclamation-circle"></i> {{ rangeError }}
    </div>

    <div v-if="store.error" class="alert alert-error">
      <i class="fas fa-exclamation-circle"></i> {{ store.error }}
      <button class="btn-close" @click="store.clearError()">×</button>
    </div>

    <div v-if="!store.loading && store.dataPoints.length === 0 && hasSearched" class="empty-state">
      <i class="fas fa-chart-bar"></i>
      <p>No analytics data found for the selected period.</p>
      <p class="text-muted">Run <code>app:analytics:aggregate</code> to generate data.</p>
    </div>

    <template v-if="store.dataPoints.length > 0">
      <!-- Success Rate Chart -->
      <div class="chart-card">
        <h3>Daily Success Rate per Mailbox</h3>
        <LineChart :data="successRateChartData" :height="280" />
      </div>

      <!-- Anomaly Count Chart -->
      <div class="chart-card">
        <h3>Daily Anomaly Count Over Time</h3>
        <LineChart :data="anomalyChartData" :height="280" />
      </div>

      <!-- Confidence Score Distribution -->
      <div class="chart-card">
        <h3>Confidence Score Distribution</h3>
        <BarChart :data="confidenceChartData" :height="280" />
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useAnalyticsStore } from '../stores/analytics.js'
import { useEmailAccountsStore } from '../stores/emailAccounts.js'
import LineChart from '../components/charts/LineChart.vue'
import BarChart from '../components/charts/BarChart.vue'

const store = useAnalyticsStore()
const emailAccountsStore = useEmailAccountsStore()

const today = new Date().toISOString().slice(0, 10)
const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10)

const from = ref(thirtyDaysAgo)
const to = ref(today)
const selectedEmailAccountId = ref(null)
const hasSearched = ref(false)
const rangeError = ref(null)

const emailAccounts = computed(() => emailAccountsStore.accounts)

onMounted(async () => {
  if (!emailAccountsStore.accounts.length) {
    await emailAccountsStore.loadAccounts()
  }
  await loadAnalytics()
})

async function loadAnalytics() {
  rangeError.value = null
  if (!from.value || !to.value) return

  const fromDate = new Date(from.value)
  const toDate = new Date(to.value)
  const diff = Math.round((toDate - fromDate) / 86400000)

  if (diff > 90) {
    rangeError.value = 'Date range cannot exceed 90 days.'
    return
  }
  if (diff < 0) {
    rangeError.value = '"To" date must be on or after "From" date.'
    return
  }

  hasSearched.value = true
  await store.fetchAnalytics(from.value, to.value, selectedEmailAccountId.value)
}

async function exportCsv() {
  await store.exportCsv(from.value, to.value, selectedEmailAccountId.value)
}

// Aggregate data points by date and mailbox for charts
const uniqueDates = computed(() => {
  const dates = [...new Set(store.dataPoints.map(dp => dp.date))]
  return dates.sort()
})

const uniqueMailboxes = computed(() => {
  const seen = new Map()
  store.dataPoints.forEach(dp => {
    if (!seen.has(dp.emailAccountId)) {
      seen.set(dp.emailAccountId, dp.emailAccountLabel)
    }
  })
  return [...seen.entries()].map(([id, label]) => ({ id, label }))
})

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16']

const successRateChartData = computed(() => {
  const datasets = uniqueMailboxes.value.map((mb, i) => {
    const data = uniqueDates.value.map(date => {
      const dp = store.dataPoints.find(d => d.date === date && d.emailAccountId === mb.id)
      if (!dp || dp.totalProcessed === 0) return null
      return Math.round((dp.successCount / dp.totalProcessed) * 100 * 10) / 10
    })
    return {
      label: mb.label,
      data,
      borderColor: COLORS[i % COLORS.length],
      backgroundColor: COLORS[i % COLORS.length] + '22',
      fill: false,
      tension: 0.3,
      spanGaps: true,
    }
  })
  return { labels: uniqueDates.value, datasets }
})

const anomalyChartData = computed(() => {
  const datasets = uniqueMailboxes.value.map((mb, i) => {
    const data = uniqueDates.value.map(date => {
      const dp = store.dataPoints.find(d => d.date === date && d.emailAccountId === mb.id)
      return dp ? dp.anomalyCount : null
    })
    return {
      label: mb.label,
      data,
      borderColor: COLORS[i % COLORS.length],
      backgroundColor: COLORS[i % COLORS.length] + '22',
      fill: false,
      tension: 0.3,
      spanGaps: true,
    }
  })
  return { labels: uniqueDates.value, datasets }
})

const confidenceChartData = computed(() => {
  const buckets = ['0–0.25', '0.25–0.5', '0.5–0.75', '0.75–1.0']
  const counts = [0, 0, 0, 0]

  store.dataPoints.forEach(dp => {
    if (dp.avgConfidenceScore === null) return
    const score = dp.avgConfidenceScore
    if (score < 0.25) counts[0]++
    else if (score < 0.5) counts[1]++
    else if (score < 0.75) counts[2]++
    else counts[3]++
  })

  return {
    labels: buckets,
    datasets: [{
      label: 'Data Points',
      data: counts,
      backgroundColor: ['#ef4444aa', '#f59e0baa', '#3b82f6aa', '#10b981aa'],
      borderColor: ['#ef4444', '#f59e0b', '#3b82f6', '#10b981'],
      borderWidth: 1,
    }],
  }
})
</script>

<style scoped>
.analytics-page { padding: 24px; }
.page-header { margin-bottom: 24px; }
.page-header h1 { font-size: 1.5rem; font-weight: 600; margin: 0 0 4px; }
.page-header p { margin: 0; color: #6b7280; font-size: 0.9rem; }
.filters-bar { display: flex; align-items: flex-end; gap: 16px; flex-wrap: wrap; margin-bottom: 20px; padding: 16px; background: #f9fafb; border-radius: 8px; border: 1px solid #e5e7eb; }
.filter-group { display: flex; flex-direction: column; gap: 4px; }
.filter-group label { font-size: 0.8rem; font-weight: 500; color: #374151; }
.filter-actions { display: flex; gap: 8px; align-items: flex-end; }
.form-control { padding: 7px 10px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 0.875rem; }
.alert { padding: 12px 16px; border-radius: 6px; margin-bottom: 16px; display: flex; align-items: center; gap: 8px; }
.alert-error { background: #fee2e2; color: #991b1b; border: 1px solid #fca5a5; }
.empty-state { text-align: center; padding: 48px; color: #6b7280; }
.empty-state i { font-size: 3rem; margin-bottom: 16px; display: block; }
.text-muted { color: #9ca3af; }
.chart-card { background: white; border: 1px solid #e5e7eb; border-radius: 8px; padding: 20px; margin-bottom: 20px; }
.chart-card h3 { margin: 0 0 16px; font-size: 1rem; font-weight: 600; color: #374151; }
.btn { padding: 8px 16px; border: none; border-radius: 6px; cursor: pointer; font-size: 0.875rem; font-weight: 500; display: inline-flex; align-items: center; gap: 6px; }
.btn:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-primary { background: #3b82f6; color: white; }
.btn-secondary { background: #e5e7eb; color: #374151; }
.btn-close { background: none; border: none; font-size: 1.2rem; cursor: pointer; color: #6b7280; margin-left: auto; }
.filter-group { min-width: 0; }
.chart-card { min-width: 0; }
/* Les graphiques (maintainAspectRatio: false) ont besoin d'une hauteur explicite */
.chart-card :deep(.chart-container) { height: 280px; }
.alert { overflow-wrap: anywhere; }

@media (max-width: 991.98px) {
  .analytics-page { padding: 0; }
}

@media (max-width: 767.98px) {
  .page-header { text-align: left; margin-bottom: 16px; }
  .filters-bar { gap: 12px; padding: 12px; }
  .filter-group { flex: 1 1 140px; }
  .filter-group .form-control { width: 100%; box-sizing: border-box; min-height: 44px; }
  .filter-actions { flex: 1 1 100%; flex-wrap: wrap; }
  .filter-actions .btn { flex: 1 1 auto; justify-content: center; min-height: 44px; }
  .chart-card { padding: 14px; }
  .chart-card :deep(.chart-container) { height: 220px; }
  .empty-state { padding: 32px 12px; }
  .empty-state code { overflow-wrap: anywhere; }
}

@media (pointer: coarse) {
  .btn-close { min-width: 44px; min-height: 44px; }
}
</style>
